(() => {
  const PAGINATION_SELECTOR = [
    '.buddy-benefit-slider__dots',
    '.buddy-lp-multimedia-carousel__dots',
    '.buddy-ingredient-slider__dots',
    '.hw__review-wrapper .flickity-page-dots',
    '.slideshow .flickity-page-dots',
  ].join(',');

  const INDICATOR_SELECTOR = [
    '.buddy-benefit-slider__dot',
    '.buddy-lp-multimedia-carousel__dot',
    '.buddy-ingredient-slider__dot',
    '.flickity-page-dot',
  ].join(',');

  const ACTIVE_SELECTOR = [
    '.is-active',
    '.is-selected',
    '[aria-selected="true"]',
    '[aria-current="true"]',
  ].join(',');

  const COMPACT_THRESHOLD = 10;
  const MOBILE_VISIBLE_COUNT = 7;
  const DESKTOP_VISIBLE_COUNT = 9;
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const paginationControllers = new Set();
  const paginationByContainer = new WeakMap();

  class CarouselPaginationWindow {
    constructor(container) {
      this.container = container;
      this.refreshQueued = false;
      this.refreshFrame = null;
      this.observer = new MutationObserver(() => this.queueRefresh());
      this.observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-current', 'aria-selected'],
      });
      this.refresh();
    }

    getIndicators() {
      return Array.from(this.container.children).filter((element) => element.matches(INDICATOR_SELECTOR));
    }

    queueRefresh() {
      if (this.refreshQueued) return;

      this.refreshQueued = true;
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = null;
        this.refreshQueued = false;
        this.refresh();
      });
    }

    refresh() {
      const indicators = this.getIndicators();
      const isCompact = indicators.length > COMPACT_THRESHOLD;
      const activeIndex = Math.max(0, indicators.findIndex((indicator) => indicator.matches(ACTIVE_SELECTOR)));

      this.container.classList.toggle('buddy-carousel-pagination--compact', isCompact);
      this.container.toggleAttribute('data-buddy-carousel-pagination-window', isCompact);
      this.container.style.removeProperty('--buddy-carousel-visible-indicators');

      if (!isCompact) {
        indicators.forEach((indicator) => {
          indicator.hidden = false;
          indicator.classList.remove('buddy-carousel-indicator--window-start', 'buddy-carousel-indicator--window-end');
        });
        return;
      }

      const desiredVisibleCount = mobileQuery.matches ? MOBILE_VISIBLE_COUNT : DESKTOP_VISIBLE_COUNT;
      const visibleCount = Math.min(desiredVisibleCount, indicators.length);
      const maximumStart = indicators.length - visibleCount;
      const windowStart = Math.max(0, Math.min(activeIndex - Math.floor(visibleCount / 2), maximumStart));
      const windowEnd = windowStart + visibleCount - 1;

      this.container.style.setProperty('--buddy-carousel-visible-indicators', visibleCount);
      this.container.dataset.buddyCarouselCurrent = activeIndex + 1;
      this.container.dataset.buddyCarouselTotal = indicators.length;

      indicators.forEach((indicator, index) => {
        const isVisible = index >= windowStart && index <= windowEnd;
        indicator.hidden = !isVisible;
        indicator.classList.toggle(
          'buddy-carousel-indicator--window-start',
          isVisible && index === windowStart && windowStart > 0
        );
        indicator.classList.toggle(
          'buddy-carousel-indicator--window-end',
          isVisible && index === windowEnd && windowEnd < indicators.length - 1
        );
      });
    }

    destroy() {
      this.observer.disconnect();
      if (this.refreshFrame) window.cancelAnimationFrame(this.refreshFrame);
      this.container.classList.remove('buddy-carousel-pagination--compact');
      this.container.removeAttribute('data-buddy-carousel-pagination-window');
      this.container.removeAttribute('data-buddy-carousel-current');
      this.container.removeAttribute('data-buddy-carousel-total');
      this.container.style.removeProperty('--buddy-carousel-visible-indicators');
      this.getIndicators().forEach((indicator) => {
        indicator.hidden = false;
        indicator.classList.remove('buddy-carousel-indicator--window-start', 'buddy-carousel-indicator--window-end');
      });
      paginationControllers.delete(this);
      paginationByContainer.delete(this.container);
    }
  }

  const initializePagination = (container) => {
    if (paginationByContainer.has(container)) return;

    const controller = new CarouselPaginationWindow(container);
    paginationByContainer.set(container, controller);
    paginationControllers.add(controller);
  };

  const initializePaginations = (root = document) => {
    if (root instanceof Element && root.matches(PAGINATION_SELECTOR)) initializePagination(root);
    root.querySelectorAll?.(PAGINATION_SELECTOR).forEach(initializePagination);
  };

  const destroyPaginations = (root) => {
    const containers = [];
    if (root instanceof Element && root.matches(PAGINATION_SELECTOR)) containers.push(root);
    root.querySelectorAll?.(PAGINATION_SELECTOR).forEach((container) => containers.push(container));
    containers.forEach((container) => paginationByContainer.get(container)?.destroy());
  };

  const pageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializePaginations(node);
      });
      mutation.removedNodes.forEach((node) => {
        if (node instanceof Element) destroyPaginations(node);
      });
    });
  });

  const initialize = () => {
    initializePaginations();
    pageObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  const refreshPaginations = () => paginationControllers.forEach((controller) => controller.queueRefresh());
  mobileQuery.addEventListener?.('change', refreshPaginations);
  document.addEventListener('shopify:section:load', (event) => initializePaginations(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyPaginations(event.target));
})();
